import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { DatePicker } from '../../components/ui/date-picker';
import { isMobileDevice } from '../../hooks/useIsMobile';
import {
  ChevronLeft,
  Plus,
  Calendar,
  Trash2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { Switch } from '../../components/ui/switch';
import { TimeField } from '../../components/ui/time-field';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  approved: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  rejected: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  cancelled: { bg: 'bg-zinc-500/20', text: 'text-[var(--m-text-secondary)]', border: 'border-zinc-500/30' },
};

export default function MobileLeave() {
  const navigate = useNavigate();
  const { token } = useMobileAuth();
  const api = axios.create({
    baseURL: API_URL,
    headers: { Authorization: `Bearer ${token}` },
  });

  const [entries, setEntries] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    is_full_day: true,
    start_time: '',
    end_time: '',
    reason: '',
  });

  const fetchData = useCallback(async () => {
    try {
      const [leaveRes, typesRes] = await Promise.all([
        api.get('/api/mobile/my-leave'),
        api.get('/api/mobile/leave-types'),
      ]);
      setEntries(leaveRes.data);
      setLeaveTypes(typesRes.data);
    } catch (err) {
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.leave_type_id) { toast.error('Select a leave type'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Start and end dates are required'); return; }
    if (form.start_date > form.end_date) { toast.error('End date must be after start date'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.is_full_day) { delete payload.start_time; delete payload.end_time; }
      await api.post('/api/mobile/request-leave', payload);
      toast.success('Leave request submitted for approval');
      setSheetOpen(false);
      setForm({ leave_type_id: '', start_date: '', end_date: '', is_full_day: true, start_time: '', end_time: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (entry) => {
    setConfirmDialog({
      open: true,
      title: 'Cancel Leave Request',
      description: 'Cancel this leave request?',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await api.delete(`/api/mobile/my-leave/${entry.id}`);
          toast.success('Leave request cancelled');
          fetchData();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to cancel');
        }
      },
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const todayLocal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const upcoming = entries.filter(e => e.end_date >= todayLocal && e.status !== 'rejected');
  const past = entries.filter(e => e.end_date < todayLocal || e.status === 'rejected');

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/mobile/more')} className="text-[var(--m-text-secondary)]">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h1 className="text-[var(--m-text)] font-bold text-lg">My Leave</h1>
          </div>
          <Button
            size="sm"
            className="bg-[#ffd108] text-black hover:bg-[#E5B93D] font-medium"
            onClick={() => setSheetOpen(true)}
            data-testid="request-leave-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Request
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-40" data-testid="mobile-leave-page">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
          </div>
        ) : (
          <>
            {/* Upcoming / Active */}
            {upcoming.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">Upcoming & Active</h2>
                {upcoming.map(entry => (
                  <LeaveCard key={entry.id} entry={entry} formatDate={formatDate} onCancel={handleCancel} />
                ))}
              </div>
            )}

            {/* Past */}
            {past.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">Past</h2>
                {past.map(entry => (
                  <LeaveCard key={entry.id} entry={entry} formatDate={formatDate} isPast />
                ))}
              </div>
            )}

            {entries.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="h-10 w-10 mx-auto text-zinc-600 mb-3" />
                <p className="text-[var(--m-text-secondary)] text-sm">No leave entries yet</p>
                <p className="text-[var(--m-text-muted)] text-xs mt-1">Tap "Request" to submit a leave request</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Request Leave Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="bg-[var(--m-bg)] border-[var(--m-border)] rounded-t-2xl max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-[var(--m-text)]">Request Leave</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">Leave Type <span className="text-red-500">*</span></Label>
              <Select value={form.leave_type_id} onValueChange={v => setForm({ ...form, leave_type_id: v })}>
                <SelectTrigger className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="ml-leave-type">
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {leaveTypes.map(lt => (
                    <SelectItem key={lt.id} value={lt.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: lt.color }} />
                        {lt.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">Start Date <span className="text-red-500">*</span></Label>
                {isMobileDevice() ? (
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={e => setForm({ ...form, start_date: e.target.value, end_date: (!form.is_full_day || !form.end_date) ? e.target.value : form.end_date })}
                    className="w-full h-10 px-3 rounded-md border border-[var(--m-border)] bg-[var(--m-bg-card)] text-[var(--m-text)] text-sm font-mono appearance-none"
                    data-testid="ml-start-date"
                  />
                ) : (
                  <DatePicker
                    value={form.start_date}
                    onChange={d => setForm({ ...form, start_date: d, end_date: (!form.is_full_day || !form.end_date) ? d : form.end_date })}
                    className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                    testId="ml-start-date"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">End Date <span className="text-red-500">*</span></Label>
                {isMobileDevice() ? (
                  <input
                    type="date"
                    value={form.end_date}
                    min={form.start_date}
                    onChange={e => setForm({ ...form, end_date: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-[var(--m-border)] bg-[var(--m-bg-card)] text-[var(--m-text)] text-sm font-mono appearance-none"
                    data-testid="ml-end-date"
                  />
                ) : (
                  <DatePicker
                    value={form.end_date}
                    onChange={d => setForm({ ...form, end_date: d })}
                    minDate={form.start_date}
                    className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                    testId="ml-end-date"
                  />
                )}
              </div>
            </div>
            {/* Full Day toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--m-border)] bg-[var(--m-bg-card)]">
              <Label className="text-sm text-[var(--m-text)]">Full Day</Label>
              <Switch checked={form.is_full_day} onCheckedChange={v => {
                const updates = { is_full_day: v };
                if (!v && form.start_date) updates.end_date = form.start_date;
                setForm(prev => ({ ...prev, ...updates }));
              }} />
            </div>
            {!form.is_full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">Start Time <span className="text-red-500">*</span></Label>
                  <TimeField value={form.start_time} onChange={v => setForm({ ...form, start_time: v })} />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">End Time <span className="text-red-500">*</span></Label>
                  <TimeField value={form.end_time} onChange={v => setForm({ ...form, end_time: v })} />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-widest text-[var(--m-text-muted)]">Reason</Label>
              <Textarea
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                placeholder="Optional — e.g. family holiday, doctor's appointment"
                rows={2}
                className="bg-[var(--m-bg-card)] border-[var(--m-border)]"
                data-testid="ml-reason"
              />
            </div>
            <Button
              className="w-full bg-[#ffd108] text-black hover:bg-[#E5B93D] font-medium"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="ml-submit-btn"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</> : 'Submit Leave Request'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Confirm"
        variant="destructive"
        onConfirm={confirmDialog.onConfirm}
      />
    </MobileLayout>
  );
}

function LeaveCard({ entry, formatDate, onCancel, isPast }) {
  const s = STATUS_STYLES[entry.status] || STATUS_STYLES.pending;
  const days = (() => {
    const start = new Date(entry.start_date);
    const end = new Date(entry.end_date);
    return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
  })();

  return (
    <Card className={`bg-[var(--m-bg-card)] border-[var(--m-border)] ${isPast ? 'opacity-60' : ''}`} data-testid={`leave-entry-${entry.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: entry.leave_type_color || '#71717a' }} />
              <span className="text-sm font-medium text-[var(--m-text)]">{entry.leave_type_name}</span>
              <Badge variant="outline" className={`text-[10px] ${s.bg} ${s.text} ${s.border}`}>
                {entry.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-xs text-[var(--m-text-secondary)]">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(entry.start_date)} — {formatDate(entry.end_date)}</span>
              <span className="text-zinc-600">({days} day{days > 1 ? 's' : ''})</span>
            </div>
            {entry.reason && (
              <p className="text-xs text-[var(--m-text-muted)] mt-1">{entry.reason}</p>
            )}
            {entry.status === 'rejected' && entry.rejection_reason && (
              <p className="text-xs text-red-400 mt-1">Reason: {entry.rejection_reason}</p>
            )}
          </div>
          {entry.status === 'pending' && onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCancel(entry)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
              data-testid={`cancel-leave-${entry.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
